<template>
  <div id="content">
    <div class="header col-md-12">
      <div class="header-inner row align-items-center">
        <div class="col left">
          <div class="title">
            {{ $t('sidebar.activities') }}
          </div>
        </div>
        <div class="col right">
          <div class="action">
            <base-dropdown-filter-date-range
              :type="dateType"
              :range="{ start: query.startDate, end: query.endDate }"
              :active="isAppliedFilter"
              :filterHeader="false"
              @onSubmit="handleSubmitFilter"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="content col-md-12">
      <p v-if="!ready">{{ $t('message.loading') }}</p>
      <div
        v-if="ready"
        class="row mb-3">
        <div class="col-lg-4 ml-auto">
          <label class="d-flex d-flex align-items-center">
            <span class="mr-2">
              {{ $t('message.search') }}:
            </span>
            <base-input
              v-model="query.q"
              :placeholder="$t('placeholder.searchstockactivities')"
              type="text"
              clear
              @onEnter="handleSearchEnter"
              @onClear="handleSearchClear" />
          </label>
        </div>
      </div>

      <base-table
        v-if="ready"
        :pagination="$store.state.app.pagination"
        @onPageChange="handlePageChange">

        <template #thead>
          <thead>
            <tr>
              <th>{{ $t('message.item') }}</th>
              <th>{{ $t('message.inputfrom') }}</th>
              <th>{{ $t('apps.inventory.message.stockinout') }}</th>
              <th>{{ $t('message.store') }}</th>
              <th>{{ $t('apps.inventory.message.bin') }}</th>
              <th>{{ $t('message.reference') }}</th>
              <th>{{ $t('message.saveddate') }}</th>
              <th>{{ $t('message.createdby') }}</th>
            </tr>
          </thead>
        </template>

        <template #tbody>
          <tbody>
            <tr>
              <td
                v-if="activities.length === 0"
                colspan="7"
                class="text-center">
                {{ $t('message.notfounddata') }}
              </td>
            </tr>
            <tr
              v-for="(item, index) in activities"
              :key="index">
              <td>
                <router-link-custom
                  :to="{
                    name: 'ProductEdit', query: {
                      pid: item.product.product_id,
                      pname: item.product.product_name
                    }
                  }"
                  :text="getProductFullName(item.product.product_name, item.sku.attributes)"
                  :to_appid="2"
                  :current_appid="3"
                  :disabled="item.sku.is_deleted == true" />
              </td>
              <td>
                <template>
                  <span v-if="item.stockactivity.details.reason == 'pos'">
                    {{ $t('status.inventory.reasonpos') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'recipe'">
                    {{ $t('status.inventory.reasonrecipe') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'gr'">
                    {{ $t('status.inventory.reasongr') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'gi'">
                    {{ $t('status.inventory.reasongi') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'gt'">
                    {{ $t('status.inventory.reasongt') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual recount'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }}) {{ $t('status.inventory.reasonmanualrecount')
                    }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual received'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }}) {{ $t('status.inventory.reasonmanualreceived')
                    }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual loss'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }}) {{ $t('status.inventory.reasonmanualloss') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual damage'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }}) {{ $t('status.inventory.reasonmanualdamage') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual return'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }}) {{ $t('status.inventory.reasonmanualreturn') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual transfer'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }}) {{ $t('status.inventory.reasonmanualtransfer')
                    }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual initial'">
                    {{ $t('status.inventory.reasonmanualinitial') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual create lot number'">
                    {{ $t('status.inventory.reasonmanualcreatelotnumber') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual create serial number'">
                    {{ $t('status.inventory.reasonmanualcreateserialnumber') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual update lot number'">
                    {{ $t('status.inventory.reasonmanualupdatelotnumber') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual update serial number'">
                    {{ $t('status.inventory.reasonmanualupdateserialnumber') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual delete lot number'">
                    {{ $t('status.inventory.reasonmanualdeletelotnumber') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual delete serial number'">
                    {{ $t('status.inventory.reasonmanualdeleteserialnumber') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual consumed production'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }})
                    {{ $t('status.inventory.reasonmanualconsumedproduction') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual received production'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }})
                    {{ $t('status.inventory.reasonmanualreceivedproduction') }}
                  </span>
                  <span v-if="item.stockactivity.details.reason == 'manual issue'">
                    ({{ $t('apps.inventory.message.adjustmentstock') }}) {{ $t('status.inventory.reasonmanualissue') }}
                  </span>
                </template>
              </td>
              <td>

                <template v-if="item.stockactivity.details.status == 'in'">
                  +{{ getNumber(item.stockactivity.details.quantity) }}
                </template>

                <template v-if="item.stockactivity.details.status == 'out'">
                  -{{ getNumber(item.stockactivity.details.quantity) }}
                </template>
              </td>
              <td>
                <router-link-custom
                  :to="{
                    name: 'WarehouseEdit', query: {
                      id: item.warehouse.warehouse_id,
                      name: item.warehouse.warehouse_name,
                    }
                  }"
                  :text="item.warehouse.warehouse_name"
                  :to_appid="3"
                  :current_appid="3"
                  :disabled="item.warehouse.is_deleted == true" />
              </td>
              <td>
                {{ item.warehouse.bin_code }}
              </td>
              <td>
                <!-- ref inventory -->

                <template v-if="item.stockactivity.details.ref_inventory && !item.stockactivity.details.ref_accounting">
                  <template v-if="item.stockactivity.details.ref_inventory.gr">
                    <router-link-custom
                      :to="{
                        name: 'GoodsReceiptEdit', query: {
                          gr_id: item.stockactivity.details.ref_inventory.gr.id,
                          gr_code: item.stockactivity.details.ref_inventory.gr.code
                        }
                      }"
                      :text="item.stockactivity.details.ref_inventory.gr.code"
                      :to_appid="3"
                      :current_appid="3" />
                  </template>

                  <template v-if="item.stockactivity.details.ref_inventory.gi">
                    <router-link-custom
                      :to="{
                        name: 'GoodsIssueEdit', query: {
                          gi_id: item.stockactivity.details.ref_inventory.gi.id,
                          gi_code: item.stockactivity.details.ref_inventory.gi.code
                        }
                      }"
                      :text="item.stockactivity.details.ref_inventory.gi.code"
                      :to_appid="3"
                      :current_appid="3" />
                  </template>

                  <template v-if="item.stockactivity.details.ref_inventory.gt">
                    <router-link-custom
                      :to="{
                        name: 'GoodsTransferEdit', query: {
                          gt_id: item.stockactivity.details.ref_inventory.gt.id,
                          gt_code: item.stockactivity.details.ref_inventory.gt.code
                        }
                      }"
                      :text="item.stockactivity.details.ref_inventory.gt.code"
                      :to_appid="3"
                      :current_appid="3" />
                  </template>

                  <template v-if="item.stockactivity.details.ref_inventory.lot">
                    <router-link-custom
                      :to="{
                        name: 'InventoryLotEdit', query: {
                          id: item.stockactivity.details.ref_inventory.lot.id,
                          code: item.stockactivity.details.ref_inventory.lot.code
                        }
                      }"
                      :text="item.stockactivity.details.ref_inventory.lot.code"
                      :to_appid="3"
                      :current_appid="3" />
                  </template>

                  <template v-if="item.stockactivity.details.ref_inventory.ser">
                    <router-link-custom
                      :to="{
                        name: 'InventorySerialEdit', query: {
                          id: item.stockactivity.details.ref_inventory.ser.id,
                          code: item.stockactivity.details.ref_inventory.ser.code
                        }
                      }"
                      :text="item.stockactivity.details.ref_inventory.ser.code"
                      :to_appid="3"
                      :current_appid="3" />
                  </template>
                </template>
                <!-- ref other -->

                <template v-if="item.stockactivity.details.ref_accounting">
                  <template v-if="item.stockactivity.details.ref_accounting.ps">
                    <router-link-custom
                      :to="{
                        name: 'PosTransactionDetail', query: {
                          ps_id: item.stockactivity.details.ref_accounting.ps.id,
                          ps_code: item.stockactivity.details.ref_accounting.ps.code,
                          ref: item.stockactivity.details.ref_accounting.ps.order_number
                        }
                      }"
                      :text="item.stockactivity.details.ref_accounting.ps.code"
                      :to_appid="1"
                      :current_appid="3"
                      style="float: left;" />
                  </template>

                  <template v-if="item.stockactivity.details.ref_accounting.po">
                    <router-link-custom
                      :to="{
                        name: 'PurchaseOrderEdit', query: {
                          id: item.stockactivity.details.ref_accounting.po.id,
                          code: item.stockactivity.details.ref_accounting.po.code
                        }
                      }"
                      :text="item.stockactivity.details.ref_accounting.po.code"
                      :to_appid="4"
                      :current_appid="3"
                      style="float: left;" />
                  </template>

                  <template v-if="item.stockactivity.details.ref_accounting.so">
                    <router-link-custom
                      :to="{
                        name: 'SaleOrderEdit', query: {
                          so_id: item.stockactivity.details.ref_accounting.so.id,
                          so_code: item.stockactivity.details.ref_accounting.so.code
                        }
                      }"
                      :text="item.stockactivity.details.ref_accounting.so.code"
                      :to_appid="4"
                      :current_appid="3"
                      style="float: left;" />
                  </template>
                </template>
              </td>
              <td>
                {{ getDateTime(item.stockactivity.created_at) }}
              </td>
              <td>{{ item.user.email || item.user.username }}</td>
            </tr>
          </tbody>
        </template>
      </base-table>
    </div>

    <!-- <div class="footer col-md-12">
      <div>Footer</div>
    </div> -->

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import sidebarModule from './../../sidebarModule'
import submenuModule from '../submenuModule'
import useBarcodeScanner from '../../../../use/barcodeScanner'
import { query } from 'vue-analytics'
import { FILTER_DATE_TYPE, DOWNLOAD_EXTS, DOWNLOAD_EXT_VALUE, REPORT_DATA_TYPES, REPORT_DATA_TYPE_VALUE } from '../../../../helpers/enum'
import userPermissionMixin from '@/mixins/userPermission'

export default {
  name: 'StockControl',

  mixins: [userPermissionMixin],

  setup () {
    const {
      barcode,
    } = useBarcodeScanner({ delay: 100 })

    return {
      barcode,
    }
  },

  data () {
    return {
      ready: false,
      dateType: FILTER_DATE_TYPE.DAY,
      query: {
        page: this.$route.query.page,
        q: this.$route.query.q,
        startDate: null,
        endDate: null,
      },
      isAppliedFilter: false,
    }
  },

  computed: mapGetters({
    activities: 'getActivities',
    warehouses: 'getWarehouses',
    sidebar: 'getSidebar',
    auth: 'getAuth',
  }),

  watch: {
    ready (newVal) {
      if (newVal === true) {
        // loading finish
        this.showFinish(this.$Progress)
      }
    },
    warehouses: function (newVal, oldVal) {
      sidebarModule.init(this.getObject(require('../sidebar.json')))
      sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
      sidebarModule.active({
        sidebarAtIdx: 2,
      })
    },
    $route (to, from) {
      if (!Object.keys(to.query).length) {
        this.query.page = undefined
        this.query.q = undefined
        this.query.startDate = undefined
        this.query.endDate = undefined
        this.fetchData()
      }
    },
  },

  async created () {
    // get warehouses
    if (this.warehouses.length == 0) {
      await this.$store.dispatch('getWarehouses')
    }
    // get warehouses
    this.fetchData()
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.inventory.title'),
      current: {
        app_name: 'inventory',
        app_id: 3,
        module_id: 3,
      },
    })
    // set sidebar stockcontrols ui
    sidebarModule.init(this.getObject(require('../sidebar.json')))
    sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
    sidebarModule.active({
      sidebarAtIdx: 2,
    })
  },

  mounted () {
    window.addEventListener('barcode', this.onBarcode)
  },

  destroyed: function () {
    this.leavePage(this.$Progress, ['clearActivities'])
    window.removeEventListener('barcode', this.onBarcode)
  },

  methods: {
    handleSubmitFilter ({ type, start, end }) {
      this.query.startDate = new Date(start).toISOString()
      this.query.endDate = new Date(end).toISOString()
      this.isAppliedFilter = true
      this.fetchData()
    },
    async fetchData () {
      this.updateUrl()
      this.ready = false
      await this.$store.dispatch('getActivities', {
        page: this.query.page,
        limit: this.$store.state.app.limitRowsPerpage,
        q: this.query.q,
        startDate: this.query.startDate,
        endDate: this.query.endDate,
      })
      this.ready = true
    },
    handlePageChange (pageNum = 1) {
      this.query.page = pageNum
      this.fetchData()
    },
    handleSearchEnter () {
      this.query.page = undefined
      this.fetchData()
    },
    handleSearchClear () {
      this.query.q = undefined
      this.query.page = undefined
      this.query.startDate = undefined
      this.query.endDate = undefined
      this.fetchData()
    },
    updateUrl () {
      const query = this.cleanObject(this.query)
      this.$router.push({ query }).catch(() => { })
    },
    onBarcode (event) {
      this.query.q = event.detail.code
      this.query.page = undefined
      this.query.startDate = undefined
      this.query.endDate = undefined
      this.fetchData()
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/inventory/activity/List.vue